import { Download, RefreshCw, ShieldCheck } from 'lucide-react';
import { nivelCaducidad } from '@shared/certificados';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ResumenCertificado } from '@/components/ResumenCertificado';
import { describirComprobacion } from '@/utils/comprobacionFace';
import { formatCertificateSubject } from '@/utils/certificadosService';
import type { DigitalCertificate } from '@/types';

interface Props {
  certificado: DigitalCertificate;
  onRenovar: () => void;
  onDescargar: (certificado: DigitalCertificate) => void;
  /** Instante de referencia (solo lo cambian los tests). */
  ahora?: Date;
}

/** Tarjeta «Certificado activo»: el que firma y autentica los envíos ahora mismo. */
export function TarjetaCertificadoActivo({ certificado, onRenovar, onDescargar, ahora = new Date() }: Props) {
  const { nivel, diasRestantes } = nivelCaducidad(certificado.validUntil, ahora);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-600" aria-hidden="true" />
          Certificado activo
        </CardTitle>
        <CardDescription>
          Firma las facturas y autentica los envíos a la AEAT y a FACe.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="text-lg font-semibold">{certificado.nombre}</p>
          <p className="text-sm text-muted-foreground">
            {certificado.subject ? formatCertificateSubject(certificado.subject) : 'titular desconocido'} ·{' '}
            {certificado.nif ? `NIF ${certificado.nif}` : 'sin NIF en el certificado'}
          </p>
        </div>

        <p className="text-sm">
          <ResumenCertificado
            mostrarNombre={false}
            certificado={{ nombre: certificado.nombre, validUntil: certificado.validUntil, nivel, diasRestantes }}
          />
        </p>

        <p className="text-sm text-muted-foreground">
          {certificado.comprobacionFace ?
            describirComprobacion(certificado.comprobacionFace) :
            'Sin comprobar con FACe todavía'}
        </p>

        <div className="flex flex-wrap gap-2">
          <Button onClick={onRenovar}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
            Renovar
          </Button>
          <Button variant="outline" onClick={() => onDescargar(certificado)}>
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Descargar parte pública (.pem)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
